import CommitFixToPullRequest from '~/apollo/mutations/autofix/commitFixToPullRequest.gql'
import CreateAutofixRunForPullRequestMutation from '~/apollo/mutations/autofix/createAutofixRunForPullRequest.gql'
import CreatePullRequest from '~/apollo/mutations/autofix/createPullRequest.gql'
import IgnoreCheckMetricMutation from '~/apollo/mutations/repository/ignoreCheckMetric.gql'
import CheckAllIssuesGQLQuery from '~/apollo/queries/repository/runs/run/check/allIssues.gql'
import RunAutofixableIssuesGQLQuery from '~/apollo/queries/repository/runs/run/check/autofixableIssues.gql'
import RepositoryRunCheckIssuesGQLQuery from '~/apollo/queries/repository/runs/run/check/checkIssue.gql'
import RunConcreteIssueListGQLQuery from '~/apollo/queries/repository/runs/run/check/concreteIssueList.gql'
import RepositoryRunCheckGQLQuery from '~/apollo/queries/repository/runs/run/check/detail.gql'
import RunConcreteVulnerabilityOccurrencesGQLQuery from '~/apollo/queries/repository/runs/run/scaCheck/concreteVulnsList.gql'
import RepositoryRunScaCheckGQLQuery from '~/apollo/queries/repository/runs/run/scaCheck/detail.gql'
import RepositoryRunGQLQuery from '~/apollo/queries/repository/runs/run/single.gql'

import { ActionContext, ActionTree, Module, MutationTree, Store } from 'vuex'
import { RootState } from '~/store'
import { GraphqlError, GraphqlMutationResponse, GraphqlQueryResponse } from '~/types/apolloTypes'
import {
  AnalysisIssueUnion,
  AnalysisIssueUnionConnection,
  AnalysisIssueUnionEdge,
  Check,
  CheckIssueConnection,
  CreateAutofixRunForPullRequestInput,
  CreateAutofixRunForPullRequestPayload,
  CreatePullRequestInput,
  IssueConnection,
  IssueEdge,
  IssueSeverity,
  IssueType,
  Maybe,
  PageInfo,
  Run,
  RunnerApp,
  ScaCheck,
  VulnerabilityOccurrenceConnection,
  VulnerabilityOccurrenceEdge
} from '~/types/types'

/** Extended issue node with pre-computed properties for display */
export type TransformedIssueNode = AnalysisIssueUnion & {
  shortExplanation: string
  isAiReview: boolean
  sourceCodeMarkup?: string
}

export type PageRefetchStatusT = {
  issueOccurrences: { status: boolean; page: number; issueId: string }
  runs: { status: boolean }
  runDetail: {
    status: boolean
    analyzer: string
    runId: string
    pageOffset: number
  }
}

export enum RunDetailActions {
  FETCH_RUN = 'fetchRun',
  FETCH_CHECK = 'fetchCheck',
  FETCH_SCA_CHECK = 'fetchScaCheck',
  FETCH_CHECK_ISSUES = 'fetchCheckIssues',
  FETCH_ALL_ISSUES = 'fetchAllIssues',
  FETCH_SCA_CHECK_ISSUES = 'fetchScaCheckIssues',
  FETCH_CONCRETE_ISSUE_LIST = 'fetchConcreteIssueList',
  SET_CONCRETE_ISSUE = 'setConcreteIssue',
  FETCH_AUTOFIXABLE_ISSUES = 'fetchAutofixableIssues',
  CREATE_AUTOFIX_PR = 'createAutofixPR',
  COMMIT_TO_PR = 'commitFixToPR',
  CREATE_PR = 'createPullRequest',
  IGNORE_CHECK_METRIC = 'ignoreCheckMetric'
}

export enum RunDetailMutations {
  SET_ERROR = 'setRunDetailError',
  SET_LOADING = 'setRunDetailLoading',
  SET_RUN = 'setRun',
  SET_CHECK = 'setCheck',
  SET_SCA_CHECK = 'setScaCheck',
  SET_CHECK_ISSUES = 'setCheckIssues',
  SET_ALL_ISSUES = 'setAllIssues',
  APPEND_ALL_ISSUES = 'appendAllIssues',
  CLEAR_ALL_ISSUES = 'clearAllIssues',
  SET_SCA_CHECK_ISSUES = 'setScaCheckIssues',
  SET_CONCRETE_ISSUE_LIST = 'setConcreteIssueList',
  SET_PAGE_REFETCH_STATUS = 'setPageRefetchStatus',
  SET_RUNNER_INFO = 'setRunnerInfo',
  UPDATE_CHECK_ISSUE_COUNTS = 'updateCheckIssueCounts',
  SET_AUTOFIXABLE_ISSUES = 'setAutofixableIssues'
}

export interface RunDetailModuleState {
  loading: boolean
  error: Record<string, unknown>
  run: Run
  check: Check
  scaCheck: ScaCheck
  checkIssues: CheckIssueConnection
  allIssues: AnalysisIssueUnionConnection
  allIssuesNodes: TransformedIssueNode[]
  allIssuesNodeIds: Set<string>
  scaCheckIssues: VulnerabilityOccurrenceConnection
  concreteIssueList: IssueConnection
  pageRefetchStatus: PageRefetchStatusT
  runnerInfo: RunnerApp
}

export type RunDetailActionContext = ActionContext<RunDetailModuleState, RootState>

export interface RunDetailModuleMutations extends MutationTree<RunDetailModuleState> {
  [RunDetailMutations.SET_LOADING](state: RunDetailModuleState, value: boolean): void
  [RunDetailMutations.SET_ERROR](state: RunDetailModuleState, error: GraphqlError): void
  [RunDetailMutations.SET_RUN](state: RunDetailModuleState, run: Run): void
  [RunDetailMutations.SET_CHECK](state: RunDetailModuleState, check: Check): void
  [RunDetailMutations.SET_SCA_CHECK](state: RunDetailModuleState, check: ScaCheck): void
  [RunDetailMutations.SET_CHECK_ISSUES](
    state: RunDetailModuleState,
    checkIssues: CheckIssueConnection
  ): void
  [RunDetailMutations.SET_ALL_ISSUES](
    state: RunDetailModuleState,
    allIssues: AnalysisIssueUnionConnection
  ): void
  [RunDetailMutations.APPEND_ALL_ISSUES](
    state: RunDetailModuleState,
    allIssues: AnalysisIssueUnionConnection
  ): void
  [RunDetailMutations.CLEAR_ALL_ISSUES](state: RunDetailModuleState): void
  [RunDetailMutations.SET_SCA_CHECK_ISSUES](
    state: RunDetailModuleState,
    scaCheckIssues: VulnerabilityOccurrenceConnection
  ): void
  [RunDetailMutations.SET_CONCRETE_ISSUE_LIST](
    state: RunDetailModuleState,
    concreteIssueList: IssueConnection
  ): void
  [RunDetailMutations.SET_PAGE_REFETCH_STATUS](
    state: RunDetailModuleState,
    pageRefetchStatus: PageRefetchStatusT
  ): void
  [RunDetailMutations.SET_RUNNER_INFO](state: RunDetailModuleState, runnerInfo: RunnerApp): void
  [RunDetailMutations.UPDATE_CHECK_ISSUE_COUNTS](
    state: RunDetailModuleState,
    payload: { checkId: string; decrementRaised: number; incrementSuppressed: number }
  ): void
  [RunDetailMutations.SET_AUTOFIXABLE_ISSUES](
    state: RunDetailModuleState,
    payload: {
      autofixableIssues: Check['autofixableIssues']
      filesAffectedByAutofix: Check['filesAffectedByAutofix']
    }
  ): void
}

export interface RunDetailModuleActions extends ActionTree<RunDetailModuleState, RootState> {
  [RunDetailActions.FETCH_RUN](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: {
      provider: string
      owner: string
      name: string
      runId: string
      isRunner: boolean
      refetch?: boolean
    }
  ): Promise<Run | undefined>
  [RunDetailActions.FETCH_CHECK](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: { checkId: string; refetch?: boolean }
  ): Promise<void>
  [RunDetailActions.FETCH_SCA_CHECK](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: { runId: string; targetId: string; refetch?: boolean }
  ): Promise<void>
  [RunDetailActions.FETCH_CHECK_ISSUES](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: {
      checkId: string
      shortcode: string
      currentPageNumber: number
      limit: number
      isRunner: boolean
      q?: Maybe<string>
      sort?: Maybe<string>
      refetch?: boolean
    }
  ): Promise<void>
  [RunDetailActions.FETCH_ALL_ISSUES](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: {
      checkId: string
      limit: number
      after?: Maybe<string>
      q?: Maybe<string>
      category?: IssueType
      severity?: IssueSeverity
      refetch?: boolean
    }
  ): Promise<void>
  [RunDetailActions.FETCH_SCA_CHECK_ISSUES](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: {
      runId: string
      targetId: string
      currentPageNumber: number
      limit: number
      q?: Maybe<string>
      refetch?: boolean
    }
  ): Promise<void>
  [RunDetailActions.FETCH_AUTOFIXABLE_ISSUES](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: { checkId: string; refetch?: boolean }
  ): Promise<void>
  [RunDetailActions.FETCH_CONCRETE_ISSUE_LIST](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: {
      checkId: string
      limit: number
      currentPageNumber: number
      sort?: string
      issueType?: string
      q?: string
      refetch?: boolean
    }
  ): Promise<void>
  [RunDetailActions.CREATE_AUTOFIX_PR](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: { input: CreateAutofixRunForPullRequestInput }
  ): Promise<CreateAutofixRunForPullRequestPayload>
  [RunDetailActions.COMMIT_TO_PR](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: { input: CreatePullRequestInput }
  ): Promise<unknown>
  [RunDetailActions.CREATE_PR](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: { input: CreatePullRequestInput }
  ): Promise<unknown>
  [RunDetailActions.IGNORE_CHECK_METRIC](
    this: Store<RootState>,
    { commit }: RunDetailActionContext,
    args: { checkId: string; key: string; metricShortcode: string }
  ): Promise<boolean>
}

const runDetailStoreModule: Module<RunDetailModuleState, RootState> = {
  state: (): RunDetailModuleState => ({
    loading: false,
    error: {},
    run: {} as Run,
    check: {} as Check,
    scaCheck: {} as ScaCheck,
    checkIssues: {} as CheckIssueConnection,
    allIssues: {
      pageInfo: {} as PageInfo,
      edges: [] as Array<Maybe<AnalysisIssueUnionEdge>>
    } as AnalysisIssueUnionConnection,
    allIssuesNodes: [],
    allIssuesNodeIds: new Set<string>(),
    concreteIssueList: {
      pageInfo: {} as PageInfo,
      edges: [] as Array<Maybe<IssueEdge>>
    },
    scaCheckIssues: {
      pageInfo: {} as PageInfo,
      edges: [] as Array<Maybe<VulnerabilityOccurrenceEdge>>
    },
    pageRefetchStatus: {
      runs: { status: false },
      runDetail: {
        status: false,
        analyzer: '',
        runId: '',
        pageOffset: 0
      },
      issueOccurrences: {
        status: false,
        issueId: '',
        page: 1
      }
    },
    runnerInfo: {} as RunnerApp
  }),

  mutations: {
    [RunDetailMutations.SET_LOADING](state, value) {
      state.loading = value
    },
    [RunDetailMutations.SET_ERROR](state, error) {
      state.error = error
    },
    [RunDetailMutations.SET_RUN](state, run) {
      state.run = run
    },
    [RunDetailMutations.SET_CHECK](state, check) {
      state.check = check
    },
    [RunDetailMutations.SET_SCA_CHECK](state, check) {
      state.scaCheck = check
    },
    [RunDetailMutations.SET_CHECK_ISSUES](state, checkIssues) {
      state.checkIssues = checkIssues
    },
    [RunDetailMutations.SET_ALL_ISSUES](state, allIssues) {
      const nodes: TransformedIssueNode[] = []
      const nodeIds = new Set<string>()
      if (allIssues?.edges) {
        for (const edge of allIssues.edges) {
          if (edge?.node) {
            nodes.push({
              ...edge.node,
              shortExplanation: edge.node.shortExplanation ?? '',
              isAiReview: edge.node.__typename === 'AIReviewIssue',
              sourceCodeMarkup: edge.node.sourceCodeRendered ?? undefined
            } as TransformedIssueNode)
            nodeIds.add(edge.node.id)
          }
        }
      }
      state.allIssues = allIssues
      state.allIssuesNodes = nodes
      state.allIssuesNodeIds = nodeIds
    },
    [RunDetailMutations.APPEND_ALL_ISSUES](state, newAllIssues) {
      if (!newAllIssues?.edges) return

      const newNodes: TransformedIssueNode[] = []
      const newEdges: Array<Maybe<AnalysisIssueUnionEdge>> = []

      for (const edge of newAllIssues.edges) {
        if (edge?.node && !state.allIssuesNodeIds.has(edge.node.id)) {
          newNodes.push({
            ...edge.node,
            shortExplanation: edge.node.shortExplanation ?? '',
            isAiReview: edge.node.__typename === 'AIReviewIssue',
            sourceCodeMarkup: edge.node.sourceCodeRendered ?? undefined
          } as TransformedIssueNode)
          newEdges.push(edge)
          state.allIssuesNodeIds.add(edge.node.id)
        }
      }

      state.allIssues.edges.push(...newEdges)
      state.allIssues.pageInfo = newAllIssues.pageInfo
      state.allIssues.totalCount = newAllIssues.totalCount
      state.allIssuesNodes.push(...newNodes)
    },
    [RunDetailMutations.CLEAR_ALL_ISSUES](state) {
      state.allIssues = {
        pageInfo: {} as PageInfo,
        edges: [] as Array<Maybe<AnalysisIssueUnionEdge>>
      } as AnalysisIssueUnionConnection
      state.allIssuesNodes = []
      state.allIssuesNodeIds = new Set<string>()
    },
    [RunDetailMutations.SET_SCA_CHECK_ISSUES](state, scaCheckIssues) {
      state.scaCheckIssues = scaCheckIssues
    },
    [RunDetailMutations.SET_CONCRETE_ISSUE_LIST](state, concreteIssueList) {
      state.concreteIssueList = concreteIssueList
    },
    [RunDetailMutations.SET_PAGE_REFETCH_STATUS](state, pageRefetchStatus) {
      state.pageRefetchStatus = Object.assign(state.pageRefetchStatus, pageRefetchStatus)
    },
    [RunDetailMutations.SET_RUNNER_INFO](state, runnerInfo) {
      state.runnerInfo = runnerInfo
    },
    [RunDetailMutations.UPDATE_CHECK_ISSUE_COUNTS](
      state,
      { checkId, decrementRaised, incrementSuppressed }
    ) {
      // Update the check in run.checks
      if (state.run.checks?.edges) {
        state.run.checks.edges = state.run.checks.edges.map((edge) => {
          if (edge?.node?.id === checkId) {
            return {
              ...edge,
              node: {
                ...edge.node,
                issuesRaisedCount: Math.max(
                  0,
                  (edge.node.issuesRaisedCount ?? 0) - decrementRaised
                ),
                issuesSuppressedCount: (edge.node.issuesSuppressedCount ?? 0) + incrementSuppressed
              }
            }
          }
          return edge
        })
      }
    },
    [RunDetailMutations.SET_AUTOFIXABLE_ISSUES](
      state,
      { autofixableIssues, filesAffectedByAutofix }
    ) {
      if (state.check) {
        state.check = {
          ...state.check,
          autofixableIssues,
          filesAffectedByAutofix
        }
      }
    }
  } as RunDetailModuleMutations,

  actions: {
    async [RunDetailActions.FETCH_RUN]({ commit }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      try {
        const response: GraphqlQueryResponse = await this.$fetchGraphqlData(
          RepositoryRunGQLQuery,
          {
            provider: this.$providerMetaMap[args.provider].value,
            owner: args.owner,
            name: args.name,
            runId: args.runId,
            isRunner: args.isRunner
          },
          args.refetch
        )
        commit(RunDetailMutations.SET_RUN, response.data.repository?.run)

        if (args.isRunner) {
          commit(
            RunDetailMutations.SET_RUNNER_INFO,
            response.data.repository?.owner.runnerApp ?? {}
          )
        }

        commit(RunDetailMutations.SET_LOADING, false)
        commit(RunDetailMutations.SET_ERROR, {})
        return response.data.repository?.run as Run
      } catch (e) {
        commit(RunDetailMutations.SET_ERROR, e)
        commit(RunDetailMutations.SET_LOADING, false)
        return undefined
      }
    },
    async [RunDetailActions.FETCH_CHECK]({ commit }, { checkId, refetch }) {
      commit(RunDetailMutations.SET_LOADING, true)
      try {
        const response = await this.$fetchGraphqlData(
          RepositoryRunCheckGQLQuery,
          { checkId },
          refetch
        )
        const check = response.data.check as Check
        commit(RunDetailMutations.SET_CHECK, check)
      } catch (e) {
        commit(RunDetailMutations.SET_ERROR, e)
      } finally {
        commit(RunDetailMutations.SET_LOADING, false)
      }
    },
    async [RunDetailActions.FETCH_SCA_CHECK]({ commit }, { runId, targetId, refetch }) {
      commit(RunDetailMutations.SET_LOADING, true)
      try {
        const response = await this.$fetchGraphqlData(
          RepositoryRunScaCheckGQLQuery,
          { runId, targetId },
          refetch
        )
        const check = response.data.scaCheck as ScaCheck
        commit(RunDetailMutations.SET_SCA_CHECK, check)
      } catch (e) {
        commit(RunDetailMutations.SET_ERROR, e)
      } finally {
        commit(RunDetailMutations.SET_LOADING, false)
      }
    },
    async [RunDetailActions.FETCH_CHECK_ISSUES]({ commit, state }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      await this.$fetchGraphqlData(
        RepositoryRunCheckIssuesGQLQuery,
        {
          checkId: args.checkId,
          shortcode: args.shortcode,
          limit: args.limit,
          after: this.$getGQLAfter(args.currentPageNumber, args.limit),
          isRunner: args.isRunner,
          q: args.q,
          sort: args.sort
        },
        args.refetch
      )
        .then((response: GraphqlQueryResponse) => {
          const newCheckIssues = response.data.checkIssues

          // If refetch is false, append new edges to existing data (infinite scroll)
          if (!args.refetch && state.checkIssues && newCheckIssues) {
            const mergedCheckIssues = {
              ...newCheckIssues,
              edges: [...state.checkIssues.edges, ...newCheckIssues.edges]
            }
            commit(RunDetailMutations.SET_CHECK_ISSUES, mergedCheckIssues)
          } else {
            // Replace data (initial load or explicit refetch)
            commit(RunDetailMutations.SET_CHECK_ISSUES, newCheckIssues)
          }

          commit(RunDetailMutations.SET_LOADING, false)
        })
        .catch((e: GraphqlError) => {
          commit(RunDetailMutations.SET_ERROR, e)
          commit(RunDetailMutations.SET_LOADING, false)
        })
    },
    async [RunDetailActions.FETCH_ALL_ISSUES]({ commit, state }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      try {
        const response: GraphqlQueryResponse = await this.$fetchGraphqlData(
          CheckAllIssuesGQLQuery,
          {
            checkId: args.checkId,
            limit: args.limit,
            after: args.after,
            q: args.q,
            category: args.category,
            severity: args.severity
          },
          args.refetch
        )

        const newAllIssues = response.data.check?.allIssues

        if (!args.refetch && state.allIssuesNodes.length && newAllIssues) {
          commit(RunDetailMutations.APPEND_ALL_ISSUES, newAllIssues)
        } else {
          commit(RunDetailMutations.SET_ALL_ISSUES, newAllIssues)
        }

        commit(RunDetailMutations.SET_LOADING, false)
      } catch (e) {
        commit(RunDetailMutations.SET_ERROR, e as GraphqlError)
        commit(RunDetailMutations.SET_LOADING, false)
      }
    },
    async [RunDetailActions.FETCH_SCA_CHECK_ISSUES]({ commit }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      const { refetch, ...variables } = args
      await this.$fetchGraphqlData(RunConcreteVulnerabilityOccurrencesGQLQuery, variables, refetch)
        .then((response: GraphqlQueryResponse) => {
          commit(
            RunDetailMutations.SET_SCA_CHECK_ISSUES,
            response.data.scaCheck.vulnerabilityOccurrences
          )
          commit(RunDetailMutations.SET_LOADING, false)
        })
        .catch((e: GraphqlError) => {
          commit(RunDetailMutations.SET_ERROR, e)
          commit(RunDetailMutations.SET_LOADING, false)
        })
    },
    async [RunDetailActions.FETCH_AUTOFIXABLE_ISSUES]({ commit }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      await this.$fetchGraphqlData(
        RunAutofixableIssuesGQLQuery,
        {
          checkId: args.checkId
        },
        args.refetch
      )
        .then((response: GraphqlQueryResponse) => {
          const check = response.data.check
          commit(RunDetailMutations.SET_AUTOFIXABLE_ISSUES, {
            autofixableIssues: check.autofixableIssues,
            filesAffectedByAutofix: check.filesAffectedByAutofix
          })
          commit(RunDetailMutations.SET_LOADING, false)
        })
        .catch((e: GraphqlError) => {
          commit(RunDetailMutations.SET_ERROR, e)
          commit(RunDetailMutations.SET_LOADING, false)
        })
    },
    async [RunDetailActions.FETCH_CONCRETE_ISSUE_LIST]({ commit }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      try {
        const response = await this.$fetchGraphqlData(
          RunConcreteIssueListGQLQuery,
          args,
          args.refetch
        )
        commit(RunDetailMutations.SET_CONCRETE_ISSUE_LIST, response.data.check?.concreteIssues)
      } catch (e) {
        commit(RunDetailMutations.SET_ERROR, e)
      } finally {
        commit(RunDetailMutations.SET_LOADING, false)
      }
    },
    async [RunDetailActions.CREATE_AUTOFIX_PR]({ commit }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      const response = await this.$applyGraphqlMutation(CreateAutofixRunForPullRequestMutation, {
        input: args.input
      })

      commit(RunDetailMutations.SET_LOADING, false)
      return response.data.createAutofixRunForPullRequest as CreateAutofixRunForPullRequestPayload
    },
    async [RunDetailActions.COMMIT_TO_PR]({ commit }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      const response = await this.$applyGraphqlMutation(CommitFixToPullRequest, {
        input: args.input
      })
      commit(RunDetailMutations.SET_LOADING, false)
      return response
    },
    async [RunDetailActions.CREATE_PR]({ commit }, args) {
      commit(RunDetailMutations.SET_LOADING, true)
      const response = await this.$applyGraphqlMutation(CreatePullRequest, {
        input: args.input
      })
      commit(RunDetailMutations.SET_LOADING, false)
      return response
    },
    async [RunDetailActions.IGNORE_CHECK_METRIC](_, { checkId, key, metricShortcode }) {
      try {
        const response = (await this.$applyGraphqlMutation(IgnoreCheckMetricMutation, {
          checkId,
          key,
          metricShortcode
        })) as GraphqlMutationResponse

        return Boolean(response.data?.ignoreCheckMetric?.ok)
      } catch (e) {
        this.$logErrorAndToast(e as Error, 'An error occurred while ignoring metric.')
      }
      return false
    }
  } as RunDetailModuleActions
}

export default runDetailStoreModule
